/**
 * What moves many times a second while a microphone is open — a timer, a meter, counted speech —
 * kept out of the screen's state: a store only the component that draws it subscribes to
 * (`useReadout`), so the rest of the screen renders when the phase changes, not on every tick.
 */
import { useSyncExternalStore } from 'react'

/** A readout as a component follows it. */
export interface ReadoutSource<T> {
  subscribe: (listener: () => void) => () => void
  get: () => T
}

export class ReadoutStore<T> implements ReadoutSource<T> {
  private readonly listeners = new Set<() => void>()

  constructor(private readout: T) {}

  readonly get = (): T => this.readout

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  readonly set = (readout: T): void => {
    this.readout = readout
    for (const listener of this.listeners) listener()
  }
}

/** The readout as it moves: the component that calls this renders on every tick. */
export function useReadout<T>(source: ReadoutSource<T>): T {
  return useSyncExternalStore(source.subscribe, source.get, source.get)
}
